import { ampli } from '@/ampli';
import Decimal from 'decimal.js-light';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { formatUnits } from 'viem';
import { useBalance as useEthereumBalance } from 'wagmi';

import type { CrossChainQuote } from '@/lib/api/0x-cross-chain/types';
import { isTokenRestrictedInCountry } from '@/lib/geo/restrictedIssuers';

import { InlineAlertBox } from '@/components/TransactionModule/Alerts/InlineAlertBox';
import { PriceImpactAlert } from '@/components/TransactionModule/Alerts/PriceImpactAlert';
import { SlippageAlert } from '@/components/TransactionModule/Alerts/SlippageAlert';
import { AmountInputGroup } from '@/components/TransactionModule/AmountInputGroup';
import { ConnectedWalletLine } from '@/components/TransactionModule/CrossChainMode/ConnectedWalletLine';
import {
  getAmount,
  getCanBalanceCoverNetworkFees,
  getNetworkFeeReserve,
  isApprovalRequired,
} from '@/components/TransactionModule/CrossChainMode/utils';
import { ExchangeRate } from '@/components/TransactionModule/ExchangeRate';
import { getTokenRate } from '@/components/TransactionModule/ExchangeRate/utils';
import {
  priceImpactDefault,
  priceImpactNegative,
  priceImpactPositive,
} from '@/components/TransactionModule/inputStyles';
import { ReceiveRecapLineCrossChainMode } from '@/components/TransactionModule/MarketMode/ReceiveRecapLine';
import { SwapDetailsAccordionCrossChainMode } from '@/components/TransactionModule/MarketMode/SwapDetailsAccordion';
import { getTokenInputValues } from '@/components/TransactionModule/MarketMode/utils';
import { PlaceOrderButton } from '@/components/TransactionModule/PlaceOrderButton';
import {
  BalanceFraction,
  SellTokenQuickActions,
} from '@/components/TransactionModule/SellTokenQuickActions';
import { TooltipPriceImpact } from '@/components/TransactionModule/SwapDetailsAccordion/Tooltips/PriceImpact';
import { SwitchTokensButton } from '@/components/TransactionModule/SwitchTokensButton';
import { useTokenBalanceValue } from '@/components/TransactionModule/TMTokenBalance';

import { SOL_ADDRESS_SOLANA } from '@/constants/addresses';
import {
  CROSS_CHAIN_SUPPORTED_CHAINS,
  NATIVE_TOKEN_SYMBOL_PER_CHAIN,
  NETWORK_NAME_PER_CHAIN_ID,
  solana,
  SUPPORTED_CHAIN_IDS_CROSS_CHAIN,
} from '@/constants/chain';
import { ZERO_X_CROSS_CHAIN_ERROR } from '@/constants/errors';
import { Issuers } from '@/constants/issuers';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useGeoCheck } from '@/hooks/useGeoCheck';
import { useIsAccountAbstractionWallet } from '@/hooks/useIsAccountAbstractionWallet';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useCrossChainResolvedSlippage } from '@/hooks/useResolvedSlippage';
import { useSplTokenBalance } from '@/hooks/useSplTokenBalance';
import { hasRequiredPriceParams } from '@/hooks/useSwapPrice';
import { useWalletConnectionModal } from '@/hooks/useWalletConnectionModal';

import {
  pastedReceivingWalletAddressSelector,
  slippageSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { isTokenBlockedByTokenList } from '@/utils/0x/token-blocking';
import type { TokenBase } from '@/utils/0x/token-registry.types';
import { SEARCH_TYPES } from '@/utils/amplitude';
import { classNames } from '@/utils/classnames';
import { PRICE_IMPACT_THRESHOLD_CROSS_CHAIN, SLIPPAGE_THRESHOLDS } from '@/utils/errors';
import type { ChainAddress } from '@/utils/models';
import { isEthereumAddress, isSolanaAddress } from '@/utils/models';
import { isArcUsdc, isTokenAddressNativeAsset } from '@/utils/multichain';
import { isNullAmount } from '@/utils/numbers';
import { commify } from '@/utils/string';
import { isDestinationChainUnsupportedByWallet } from '@/utils/wallet';

import { useModuleContext } from '../ModuleContext';
import type { CrossChainTradeQuote, TokenUsdPrices } from '../tradeQuote';
import { useTradeEngine } from '../useTradeEngine';
import { useTradeExecutor } from '../useTradeExecutor';
import { useTradeInputs } from '../useTradeInputs';
import { TokenBalanceLine } from './TokenBalanceLine';
import { TokenInfoButton } from './TokenInfoButton';
import { TokenSelect } from './TokenSelect';
import { ActionButton } from './TradeCta';

const DeferredPasteOrConnectWalletModal = dynamic(
  () =>
    import('@/components/TransactionModule/CrossChainMode/PasteOrConnectWalletModal').then(
      ({ PasteOrConnectWalletModal }) => PasteOrConnectWalletModal,
    ),
  {},
);

const Divider = () => <div className="relative z-1 min-h-px bg-token-gray-300 h-px w-full" />;
function HighLowSlippageAlert({ slippage }: { slippage: number | undefined }) {
  if (!slippage || Number.isNaN(slippage)) return null;

  const slippageType =
    slippage < SLIPPAGE_THRESHOLDS[0]
      ? 'low'
      : slippage > SLIPPAGE_THRESHOLDS[1]
        ? 'high'
        : undefined;
  if (!slippageType) return null;

  return (
    <li className="mt-3">
      <SlippageAlert slippageType={slippageType} percentage={slippage * 100} showMessage={false} />
    </li>
  );
}

/** Maps known cross-chain quote failures to alert copy; unknown reasons fall back to the generic fetch error. */
export function crossChainQuoteErrorContent(
  message: string,
  sellTokenSymbol: string | undefined,
): { title: string; description: string } {
  switch (message) {
    case ZERO_X_CROSS_CHAIN_ERROR.INSUFFICIENT_INPUT_AMOUNT:
      return {
        title: 'Minimum amount not met',
        description: `Insufficient ${sellTokenSymbol} amounts to cover fees.`,
      };
    case ZERO_X_CROSS_CHAIN_ERROR.INSUFFICIENT_LIQUIDITY:
      return {
        title: 'Insufficient liquidity',
        description:
          'There is not enough liquidity available for this trade. Try lowering your sell amount.',
      };
    case ZERO_X_CROSS_CHAIN_ERROR.NO_QUOTES_AVAILABLE:
      return {
        title: 'No route available',
        description:
          'No route available. Try to increase the sell amount or select different tokens.',
      };
    default:
      return { title: 'Quote error', description: 'Failed fetching the swap quote. Try again.' };
  }
}

/** Renders the cross-chain form when the engine selects the cross-chain lane. */
export function CrossChainPanel() {
  const engine = useTradeEngine();
  if (engine.quote.source !== 'cross-chain') return null;

  return (
    <CrossChainPanelContent
      crossChainQuote={engine.quote}
      tokenUsdPrices={engine.tokenUsdPrices}
      gasProbeQuote={engine.gasProbeQuote}
    />
  );
}

function CrossChainPanelContent({
  crossChainQuote,
  tokenUsdPrices,
  gasProbeQuote,
}: {
  crossChainQuote: CrossChainTradeQuote;
  tokenUsdPrices: TokenUsdPrices;
  gasProbeQuote: CrossChainQuote | undefined;
}) {
  const {
    variant,
    sellToken,
    buyToken,
    accountAddress,
    onSelectToken,
    onSelectPair,
    onFlipSelectedTokens,
  } = useModuleContext();
  const quote = crossChainQuote.raw;
  const quoteError = crossChainQuote.error;
  const isLoadingQuote = crossChainQuote.status === 'loading';
  const priceImpact = crossChainQuote.priceImpact ?? { percent: 0, usd: 0 };
  const { submit, approveCrossChainSpend, isSubmitting } = useTradeExecutor({
    sellToken,
    buyToken,
  });
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const { startConnectFlow } = useWalletConnectionModal();
  const { formatFiat } = useFiatDisplay();
  const { bps: crossChainSlippageBps } = useCrossChainResolvedSlippage({ sellToken, buyToken });
  const { pastedReceivingWalletAddress, setPastedReceivingWalletAddress } =
    useMarketTradeSettingsStore(pastedReceivingWalletAddressSelector);
  const { slippage, slippageMode } = useMarketTradeSettingsStore(slippageSelector);
  const [pasteOrConnectWalletModalOpen, setPasteOrConnectWalletModalOpen] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<{ sell: Decimal; buy: Decimal }>();
  const { sellInput, buyInput, setInputMode, updateSellInput, updateBuyInput, updateInputs } =
    useTradeInputs();

  const isAccountAbstractionWallet = useIsAccountAbstractionWallet({
    chainId: sellToken?.chainId,
  });
  const isBuyChainSuppportedByAAWallet = useIsAccountAbstractionWallet({
    chainId: buyToken?.chainId,
  });
  const sellSideWallet = sellToken
    ? sellToken.chainId === solana.id
      ? solanaWallet
      : ethereumWallet
    : undefined;
  const buySideWallet =
    buyToken && !pastedReceivingWalletAddress
      ? buyToken.chainId === solana.id
        ? solanaWallet
        : ethereumWallet
      : undefined;
  const sellSideWalletAddress = sellSideWallet?.address as ChainAddress | undefined;
  const buySideWalletAddress = buySideWallet
    ? (buySideWallet.address as ChainAddress)
    : pastedReceivingWalletAddress;

  const sellAmount = useMemo(
    () => getAmount(sellInput, sellToken?.decimals),
    [sellInput, sellToken?.decimals],
  );
  const sellTokenBalance = useTokenBalanceValue({
    accountAddress,
    chainId: sellToken?.chainId,
    tokenAddress: sellToken?.address,
  });
  const isSellAmountGreaterThanBalance = Boolean(
    sellAmount && sellTokenBalance && BigInt(sellAmount) > sellTokenBalance.value,
  );

  useEffect(() => {
    if (!crossChainQuote.buyAmount || !buyToken || !sellToken) return;
    const grossBuyAmountDecimal = formatUnits(BigInt(crossChainQuote.buyAmount), buyToken.decimals);
    const buyAmountUsd = new Decimal(grossBuyAmountDecimal).mul(tokenUsdPrices.buyTokenUsd);
    updateBuyInput(
      getTokenInputValues(grossBuyAmountDecimal, buyToken.decimals, buyAmountUsd.toNumber()),
    );
    setExchangeRate(
      getTokenRate({
        buyAmount: Number(grossBuyAmountDecimal),
        sellAmount: Number(sellInput.decimal),
      }),
    );
  }, [
    buyToken,
    crossChainQuote.buyAmount,
    sellInput.decimal,
    sellToken,
    tokenUsdPrices.buyTokenUsd,
    updateBuyInput,
  ]);

  useEffect(() => {
    if (quoteError) updateBuyInput();
  }, [quoteError, updateBuyInput]);

  const flipSelectedBuySellTokens = useCallback(() => {
    onFlipSelectedTokens();
    updateInputs();
  }, [onFlipSelectedTokens, updateInputs]);

  const flipToPair = useCallback(
    (nextBuyToken: TokenBase, nextSellToken: TokenBase) => {
      onSelectPair(nextBuyToken, nextSellToken);
      updateInputs();
    },
    [onSelectPair, updateInputs],
  );

  const handleSelectToken = useCallback(
    (selectedToken: TokenBase, side: 'buy' | 'sell') => {
      const isBuyToken = side === 'buy';
      const previousToken = isBuyToken ? buyToken : sellToken;
      const otherToken = isBuyToken ? sellToken : buyToken;
      const areSameTokensSelected =
        selectedToken.address === otherToken?.address &&
        selectedToken.chainId === otherToken?.chainId;
      const areEquivalentTokensSelected =
        isArcUsdc(selectedToken.address, selectedToken.chainId) &&
        isArcUsdc(otherToken?.address, otherToken?.chainId);

      updateBuyInput();
      if (!isBuyToken) {
        updateSellInput(
          getTokenInputValues(sellInput.decimal, selectedToken.decimals, sellInput.usd),
        );
      }

      if (areSameTokensSelected) {
        flipSelectedBuySellTokens();
      } else if (previousToken && areEquivalentTokensSelected) {
        if (isBuyToken) {
          flipToPair(selectedToken, previousToken);
        } else {
          flipToPair(previousToken, selectedToken);
        }
      } else {
        onSelectToken(selectedToken, side);
      }
    },
    [
      buyToken,
      flipSelectedBuySellTokens,
      flipToPair,
      onSelectToken,
      sellInput.decimal,
      sellInput.usd,
      sellToken,
      updateBuyInput,
      updateSellInput,
    ],
  );

  // Referentially stable so `React.memo(SelectToken)` can skip its modal subtree.
  const handleSelectSellToken = useCallback(
    (token: TokenBase) => handleSelectToken(token, 'sell'),
    [handleSelectToken],
  );
  const handleSelectBuyToken = useCallback(
    (token: TokenBase) => handleSelectToken(token, 'buy'),
    [handleSelectToken],
  );

  const handleSwitchTokens = useCallback(() => {
    flipSelectedBuySellTokens();
    ampli.tokenPairFlipped({
      buy_token_symbol: buyToken?.symbol,
      sell_token_symbol: sellToken?.symbol,
    });
  }, [buyToken?.symbol, flipSelectedBuySellTokens, sellToken?.symbol]);

  const connectWalletOnClick = useCallback(() => {
    ampli.walletConnectClicked({ source: 'trade_module', ui_location: 'trade_module' });
    startConnectFlow({ source: 'trade_module' });
  }, [startConnectFlow]);

  const selectReceiveWalletOnClick = useCallback(() => {
    ampli.receiveWalletSelectorClicked();
    setPasteOrConnectWalletModalOpen(true);
  }, []);

  let priceImpactAmount = '';
  let priceImpactStyles = priceImpactDefault;
  if (Number.isFinite(priceImpact.percent) && priceImpact.percent < 10 ** 20) {
    priceImpactAmount = `(${commify(priceImpact.percent.toFixed(2))}%)`;
  }
  if (Math.abs(priceImpact.percent) > PRICE_IMPACT_THRESHOLD_CROSS_CHAIN) {
    priceImpactStyles = priceImpact.percent >= 0 ? priceImpactPositive : priceImpactNegative;
  }

  const isCrossChainSupported =
    (!sellToken || SUPPORTED_CHAIN_IDS_CROSS_CHAIN.includes(sellToken.chainId)) &&
    (!buyToken || SUPPORTED_CHAIN_IDS_CROSS_CHAIN.includes(buyToken.chainId));
  const isAAWalletNotSupported = isAccountAbstractionWallet && !isBuyChainSuppportedByAAWallet;
  const superstateTokenError =
    sellToken?.issuer?.name === Issuers.SUPERSTATE || buyToken?.issuer?.name === Issuers.SUPERSTATE;
  const { data: geoData } = useGeoCheck();
  const isBuyTokenGeoBlocked = Boolean(
    geoData && isTokenRestrictedInCountry(buyToken, geoData.country),
  );
  const isSellTokenGeoBlocked = Boolean(
    geoData && isTokenRestrictedInCountry(sellToken, geoData.country),
  );
  const isBuyTokenBlocked = isTokenBlockedByTokenList(buyToken) || isBuyTokenGeoBlocked;
  const isSellTokenBlocked = isTokenBlockedByTokenList(sellToken) || isSellTokenGeoBlocked;
  const isDestinationChainUnsupportedByConnectedWallet =
    !pastedReceivingWalletAddress &&
    !isAccountAbstractionWallet &&
    isDestinationChainUnsupportedByWallet({
      walletName: ethereumWallet?.meta.name,
      destinationChainId: buyToken?.chainId,
    });

  const isNativeSellToken = sellToken
    ? isTokenAddressNativeAsset(sellToken.address, sellToken.chainId)
    : false;
  const feeReserve = getNetworkFeeReserve(quote ?? gasProbeQuote, sellToken);
  const isSellTokenBalanceZero = sellTokenBalance?.value === 0n;
  const sellBalanceDecimal = sellTokenBalance
    ? new Decimal(formatUnits(sellTokenBalance.value, sellTokenBalance.decimals))
    : new Decimal(0);
  const isInsufficientBalanceForGas = isNativeSellToken && sellBalanceDecimal.lte(feeReserve);

  const handleBalancePercentageClick = useCallback(
    (balDenominator: BalanceFraction) => {
      setInputMode('tokenAmount');
      if (balDenominator === BalanceFraction.ZERO) {
        updateSellInput();
        return;
      }
      if (!sellTokenBalance || !sellToken) return;

      const sellTokenPrice = isNativeSellToken
        ? tokenUsdPrices.nativeTokenUsd
        : tokenUsdPrices.sellTokenUsd;
      let sellBalance = new Decimal(formatUnits(sellTokenBalance.value, sellTokenBalance.decimals));
      if (isNativeSellToken) {
        if (sellBalance.lte(feeReserve)) {
          toast.error(`Not enough ${sellToken.symbol} to cover network fees`, {
            id: 'cross-chain-balance-shortcut-error',
            className:
              '!bg-special-background-modal !border !border-token-gray-300 !text-token-gray-800 !rounded-xl',
          });
          return;
        }
        sellBalance = sellBalance.sub(feeReserve);
      }

      const amount = sellBalance.div(balDenominator);
      updateSellInput({
        decimal: amount.toString(),
        formatted: amount.toString(),
        usd: amount.mul(sellTokenPrice).toNumber(),
      });
    },
    [
      feeReserve,
      isNativeSellToken,
      sellToken,
      sellTokenBalance,
      setInputMode,
      tokenUsdPrices.nativeTokenUsd,
      tokenUsdPrices.sellTokenUsd,
      updateSellInput,
    ],
  );

  const ethereumBalance = useEthereumBalance({
    address:
      sellToken?.chainId !== solana.id &&
      sellSideWalletAddress &&
      isEthereumAddress(sellSideWalletAddress)
        ? sellSideWalletAddress
        : undefined,
    chainId: sellToken?.chainId,
  });
  const { data: splTokenBalance } = useSplTokenBalance({
    tokenAddress: sellToken?.chainId === solana.id ? SOL_ADDRESS_SOLANA : undefined,
    accountAddress:
      sellToken?.chainId === solana.id &&
      sellSideWalletAddress &&
      isSolanaAddress(sellSideWalletAddress)
        ? sellSideWalletAddress
        : undefined,
  });
  const canBalanceCoverNetworkFees = getCanBalanceCoverNetworkFees(
    quote,
    sellToken,
    ethereumBalance.data?.value,
    splTokenBalance ? BigInt(splTokenBalance.amount) : undefined,
  );
  const shouldShowInsufficientBalanceToCoverNetworkFees =
    Boolean(quote && sellToken) && !canBalanceCoverNetworkFees;

  const shouldShowSellTokenAndActions = !!sellToken?.address && !!sellSideWalletAddress;
  const shouldShowBuyTokenBalance = !!buyToken?.address && !!buySideWalletAddress;
  const shouldShowExchangeRate = Boolean(
    !isNullAmount(Number(buyInput.decimal)) &&
      !isNullAmount(Number(sellInput.decimal)) &&
      buyToken?.symbol &&
      sellToken?.symbol,
  );
  const shouldShowSwapDetails = Boolean(
    sellToken?.chainId &&
      buyToken?.chainId &&
      quote &&
      hasRequiredPriceParams(sellToken.address, buyToken.address, sellAmount),
  );
  const shouldShowSendingReceivingWallets =
    sellToken?.chainId === solana.id || buyToken?.chainId === solana.id;
  const isSellSideWalletRequired = !!sellToken && !sellSideWallet;
  const isBuySideWalletRequired = !!buyToken && !buySideWalletAddress;
  const shouldShowActions = !!sellSideWallet;
  const hasPriceImpact = priceImpact.percent + PRICE_IMPACT_THRESHOLD_CROSS_CHAIN <= 0;
  const canSubmit = crossChainQuote.status === 'fetched';

  return (
    <>
      <Divider />
      <div className="flex flex-col justify-center px-6 py-4">
        <div className="flex justify-between mb-3">
          <div className={fontStyles({ variant: 'LABEL_SM', class: ['flex items-center'] })}>
            <div>From</div>
            {sellToken && (
              <>
                <ChainAvatar
                  chainId={sellToken.chainId}
                  alt={`${NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]} chain logo`}
                  size={12}
                  className="mr-1 ml-1.5"
                />
                <div className="text-token-gray-700">
                  {NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]}
                </div>
              </>
            )}
          </div>
          {shouldShowSellTokenAndActions && (
            <TokenBalanceLine
              accountAddress={sellSideWalletAddress}
              chainId={sellToken?.chainId}
              tokenAddress={sellToken?.address}
            />
          )}
        </div>
        <div className="flex gap-2 items-center justify-between">
          <div className="flex items-center">
            <TokenSelect
              chainId={sellToken?.chainId}
              onSelect={handleSelectSellToken}
              activeToken={sellToken}
              searchType={SEARCH_TYPES.SELL_TOKEN}
              supportedChains={CROSS_CHAIN_SUPPORTED_CHAINS}
            />
            <TokenInfoButton direction="sell" />
          </div>
          <SellTokenQuickActions
            orderType="cross_chain"
            isDisabled={Boolean(
              isLoadingQuote ||
                !isCrossChainSupported ||
                isAAWalletNotSupported ||
                superstateTokenError ||
                isInsufficientBalanceForGas,
            )}
            isTokenBalanceZero={isSellTokenBalanceZero}
            shouldShowActions={shouldShowActions}
            shouldShowClear={!!sellInput.formatted}
            handleBalancePercentageClick={handleBalancePercentageClick}
          />
        </div>
        <AmountInputGroup
          buyInput={buyInput}
          direction="sell"
          decimals={sellToken?.decimals}
          sellInput={sellInput}
          symbol={sellToken?.symbol}
          tokenUsdPrices={tokenUsdPrices}
          updateBuyInput={updateBuyInput}
          updateSellInput={updateSellInput}
          updateInputs={updateInputs}
          setInputMode={setInputMode}
        />
      </div>
      <div className="flex items-center">
        <Divider />
        <SwitchTokensButton
          isLoading={isLoadingQuote}
          direction="reverse"
          onClick={handleSwitchTokens}
        />
        <Divider />
      </div>
      <div className="flex flex-col justify-center px-6 py-4">
        <div className="flex justify-between mb-3">
          <div className={fontStyles({ variant: 'LABEL_SM', class: ['flex items-center'] })}>
            <div>To</div>
            {buyToken && (
              <>
                <ChainAvatar
                  chainId={buyToken.chainId}
                  alt={`${NETWORK_NAME_PER_CHAIN_ID[buyToken.chainId]} chain logo`}
                  size={12}
                  className="mr-1 ml-1.5"
                />
                <div className="text-token-gray-700">
                  {NETWORK_NAME_PER_CHAIN_ID[buyToken.chainId]}
                </div>
              </>
            )}
          </div>
          {shouldShowExchangeRate && buyToken && sellToken ? (
            <ExchangeRate
              buyTokenSymbol={buyToken.symbol}
              sellTokenSymbol={sellToken.symbol}
              exchangeRate={exchangeRate}
              isLoading={isLoadingQuote}
            />
          ) : (
            shouldShowBuyTokenBalance && (
              <TokenBalanceLine
                accountAddress={buySideWalletAddress}
                chainId={buyToken?.chainId}
                tokenAddress={buyToken?.address}
              />
            )
          )}
        </div>
        <div className="flex gap-2 items-center justify-between">
          <div className="flex items-center">
            <TokenSelect
              chainId={buyToken?.chainId}
              onSelect={handleSelectBuyToken}
              activeToken={buyToken}
              searchType={SEARCH_TYPES.BUY_TOKEN}
              supportedChains={CROSS_CHAIN_SUPPORTED_CHAINS}
            />
            <TokenInfoButton direction="buy" />
          </div>
        </div>
        <AmountInputGroup
          buyInput={buyInput}
          direction="buy"
          disabled
          decimals={buyToken?.decimals}
          sellInput={sellInput}
          symbol={buyToken?.symbol}
          tokenUsdPrices={tokenUsdPrices}
          updateBuyInput={updateBuyInput}
          updateSellInput={updateSellInput}
          updateInputs={updateInputs}
          setInputMode={setInputMode}
        >
          {!!buyInput.usd && (
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: [
                  'text-token-gray-800',
                  'mb-1.5',
                  'block',
                  'whitespace-nowrap',
                  'data-[disabled="true"]:opacity-50',
                ],
              })}
            >
              {formatFiat(buyInput.usd)}
              {buyToken?.address &&
                sellToken?.address &&
                typeof priceImpact.percent === 'number' &&
                !Number.isNaN(priceImpact.percent) && (
                  <MatchaTooltip>
                    <MatchaTooltipTrigger>
                      <span className={priceImpactStyles}>
                        &nbsp;
                        {priceImpactAmount}
                      </span>
                    </MatchaTooltipTrigger>
                    <MatchaTooltipContent>
                      <TooltipPriceImpact />
                    </MatchaTooltipContent>
                    <MatchaTooltipPopover sideOffset={16} />
                    <MatchaTooltipBottomSheet header="Price impact" />
                  </MatchaTooltip>
                )}
            </div>
          )}
        </AmountInputGroup>
      </div>
      {shouldShowSwapDetails && quote && (
        <>
          {shouldShowSendingReceivingWallets && sellSideWallet && (
            <>
              <Divider />
              <div className="flex flex-col justify-center">
                <ConnectedWalletLine
                  isLoading={isLoadingQuote}
                  label="Sending wallet"
                  wallet={sellSideWallet}
                  side="sell"
                />
              </div>
            </>
          )}

          {shouldShowSendingReceivingWallets && buySideWalletAddress && (
            <>
              <Divider />
              <div className="flex flex-col justify-center">
                <ConnectedWalletLine
                  isLoading={isLoadingQuote}
                  label="Receiving wallet"
                  wallet={buySideWallet}
                  pastedReceivingWalletAddress={pastedReceivingWalletAddress}
                  side="buy"
                  resetPastedReceivingWalletAddress={() =>
                    setPastedReceivingWalletAddress(undefined)
                  }
                  chainId={buyToken?.chainId}
                />
              </div>
            </>
          )}

          <Divider />
          {buyToken && (
            <ReceiveRecapLineCrossChainMode
              isLoading={isLoadingQuote}
              quote={quote}
              buyToken={buyToken}
            />
          )}
          <Divider />
          <div className="flex flex-col justify-center">
            <SwapDetailsAccordionCrossChainMode
              quote={quote}
              isLoading={isLoadingQuote}
              nativeTokenUsd={tokenUsdPrices.nativeTokenUsd}
              buyChainId={buyToken?.chainId}
              sellChainId={sellToken?.chainId}
              slippageBps={crossChainSlippageBps}
              buySideConnectedWalletAddress={ethereumWallet?.address}
            />
          </div>
        </>
      )}
      <Divider />
      <div
        className={classNames(
          'px-6 mt-6 z-10 mb-0 bottom-4 flex justify-center tablet:justify-start items-center w-full',
          { 'fixed tablet:static': variant === 'fullpage' },
        )}
      >
        {!sellSideWallet && !buySideWallet ? (
          <ActionButton onClick={connectWalletOnClick}>Connect wallet</ActionButton>
        ) : isSellSideWalletRequired ? (
          <ActionButton onClick={connectWalletOnClick}>Connect Sending Wallet</ActionButton>
        ) : isBuySideWalletRequired ? (
          <ActionButton onClick={selectReceiveWalletOnClick}>
            Select Receiving Wallet <ChevronDownIcon className="ml-1 mb-0.5 size-3.5" />
          </ActionButton>
        ) : !isCrossChainSupported ||
          isAAWalletNotSupported ||
          superstateTokenError ||
          isSellTokenBlocked ||
          isBuyTokenBlocked ? (
          <ActionButton disabled>Error with selected tokens</ActionButton>
        ) : isSellAmountGreaterThanBalance ? (
          <ActionButton disabled>{`Insufficient ${sellToken?.symbol} balance`}</ActionButton>
        ) : shouldShowInsufficientBalanceToCoverNetworkFees ? (
          <ActionButton disabled>
            {`Insufficient ${
              sellToken?.chainId ? NATIVE_TOKEN_SYMBOL_PER_CHAIN[sellToken.chainId] : 'Native'
            } balance for fees`}
          </ActionButton>
        ) : sellToken && buyToken && quote && canSubmit ? (
          <div className="flex flex-col items-center tablet:items-start w-full">
            <div className="items-center flex flex-col gap-4 px-4 tablet:px-0 w-full">
              <PlaceOrderButton
                onClickApprove={() => void approveCrossChainSpend()}
                onSubmit={() => void submit()}
                error={quoteError}
                isLoading={isSubmitting}
                hasPriceImpact={hasPriceImpact}
                needsApproval={isApprovalRequired(quote)}
                needsWalletConfirmation={isSubmitting}
                sellToken={sellToken}
                buyToken={buyToken}
                sellTokenSymbol={sellToken.symbol}
              />
            </div>
            <ul className="w-full px-4 tablet:px-0">
              {hasPriceImpact && (
                <li>
                  <PriceImpactAlert priceImpact={priceImpact} />
                </li>
              )}
              {slippageMode === 'manual' && <HighLowSlippageAlert slippage={slippage} />}
            </ul>
          </div>
        ) : (
          <ActionButton disabled>Place order</ActionButton>
        )}
      </div>

      {!isCrossChainSupported ? (
        <div className="m-6">
          <InlineAlertBox
            level="info"
            title={`Cross chain swaps are not available on ${
              sellToken && !SUPPORTED_CHAIN_IDS_CROSS_CHAIN.includes(sellToken.chainId)
                ? NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]
                : buyToken
                  ? NETWORK_NAME_PER_CHAIN_ID[buyToken.chainId]
                  : 'this network'
            }`}
            description="Select a token from a supported network to continue."
          />
        </div>
      ) : isAAWalletNotSupported ? (
        <div className="m-6">
          <InlineAlertBox
            level="info"
            title="Chain not supported"
            description={`${buyToken?.chainName} is not supported on your smart wallet.`}
          />
        </div>
      ) : isSellTokenGeoBlocked || isBuyTokenGeoBlocked ? (
        <div className="m-6">
          <InlineAlertBox
            level="info"
            title="Token Unavailable in Your Region"
            description={`${isSellTokenGeoBlocked ? sellToken?.symbol : buyToken?.symbol} is not available for trading in your region at this time.`}
          />
        </div>
      ) : isSellTokenBlocked || isBuyTokenBlocked ? (
        <div className="m-6">
          <InlineAlertBox
            level="info"
            title="Token not supported"
            description={`${isSellTokenBlocked ? sellToken?.symbol : buyToken?.symbol} is not supported at this time.`}
          />
        </div>
      ) : superstateTokenError ? (
        <div className="m-6">
          <InlineAlertBox
            level="info"
            title="Superstate Token Unsupported"
            description="Superstate tokens are not supported for cross chain orders"
          />
        </div>
      ) : quoteError ? (
        <div className="m-6">
          <InlineAlertBox {...crossChainQuoteErrorContent(quoteError.message, sellToken?.symbol)} />
        </div>
      ) : isDestinationChainUnsupportedByConnectedWallet ? (
        <div className="m-6">
          <InlineAlertBox
            level="info"
            title="This wallet may not support the destination chain"
            description="You're swapping funds to a chain that your current wallet may not support. You could still access the funds using another compatible wallet, but access may not be available directly in this wallet."
          />
        </div>
      ) : null}

      <DeferredPasteOrConnectWalletModal
        isOpen={pasteOrConnectWalletModalOpen}
        onOpenChange={setPasteOrConnectWalletModalOpen}
        onClose={() => setPasteOrConnectWalletModalOpen(false)}
        chainId={buyToken?.chainId}
        popoverClass="border-token-gray-300 bg-special-background-modal absolute h-fit rounded-[1.125rem] z-100 shadow-25 bottom-21 left-11 right-11"
      />
    </>
  );
}
